/**
 * Human labels for the codes this module renders.
 *
 * ## Why a file and not `humanise()`
 *
 * The dropdowns showed raw enum codes — `PHONE_CALL`, `BEFORE_0700`, `OWN_VEHICLE`,
 * `H0900_1200`. `shared/lib/format`'s `humanise()` would get some of them right by
 * title-casing, and would get the ones that matter wrong: `BEFORE_0700` is "Before 7am",
 * not "Before 0700", and `H1200_1500` is "Midday – 3pm", not "H1200 1500".
 *
 * ## The list here never widens the vocabulary
 *
 * Every code list is SERVED. This maps a code the server sent to words; a code with no
 * entry falls back to a title-cased version of itself, so a constant added on the server
 * renders imperfectly rather than disappearing. It can never ADD an option — the `<select>`
 * is still driven by the served array.
 */

const LABELS: Record<string, string> = {
  // ── pledge intent ───────────────────────────────────────────────────────
  COMMITTED: 'Committed — will vote',
  LIKELY: 'Likely',
  UNSURE: 'Unsure',
  UNABLE: 'Unable to vote',
  REFUSED: 'Refused to say',
  UNREACHABLE: 'Could not be reached',

  // ── time slot. The hour is the point; the code is not. ──────────────────
  BEFORE_0700: 'Before 7am',
  H0700_0900: '7am – 9am',
  H0900_1200: '9am – midday',
  H1200_1500: 'Midday – 3pm',
  H1500_1700: '3pm – 5pm (close)',
  UNDECIDED: 'Undecided',

  // ── transport ───────────────────────────────────────────────────────────
  WALK: 'Walking',
  MATATU: 'Matatu',
  BODA: 'Boda boda',
  OWN_VEHICLE: 'Own vehicle',
  SHARED_CELL: 'Sharing with the cell',
  ASSISTED: 'Needs assistance',

  // ── how it was captured ─────────────────────────────────────────────────
  FIELD_APP: 'Field app',
  WEB: 'Web',
  PHONE_CALL: 'Phone call',
  DOOR_TO_DOOR: 'Door to door',
  SELF_SERVICE: 'Self-service',
  IMPORT: 'Imported',

  // ── cells ───────────────────────────────────────────────────────────────
  PROPOSED: 'Proposed',
  FORMING: 'Forming',
  NEEDS_COORDINATOR: 'Needs a coordinator',
  ACTIVE: 'Active',
  SUSPENDED: 'Suspended',
  ARCHIVED: 'Archived',
  MEMBER: 'Member',
  COORDINATOR: 'Coordinator',
  DEPUTY: 'Deputy',
  AUTO_ASSIGNED: 'Auto-assigned',
  FIELD_CREATED: 'Created in the field',
  IMPORTED: 'Imported',
  SPLIT: 'Split from another cell',
  MERGED: 'Merged',

  // ── polling day ─────────────────────────────────────────────────────────
  NOT_STARTED: 'Not started',
  EN_ROUTE: 'On the way',
  IN_QUEUE: 'In the queue',
  VOTED: 'Voted',
  TURNED_AWAY: 'Turned away',
  REPORTED: 'Reported',
  VERIFIED: 'Verified',
  DISPUTED: 'Disputed',
  NONE: 'No queue',
  SHORT: 'Short',
  MODERATE: 'Moderate',
  LONG: 'Long',
  VERY_LONG: 'Very long',

  // ── elections ───────────────────────────────────────────────────────────
  PLANNED: 'Planned',
  ORGANISING: 'Organising',
  ENGAGING: 'Engaging',
  POLLING: 'Polling day',
  CLOSED: 'Closed',
  RECONCILED: 'Reconciled',
  GENERAL: 'General election',
  BY_ELECTION: 'By-election',
  REFERENDUM: 'Referendum',
  PRIMARY: 'Party primary',

  // ── incentives ──────────────────────────────────────────────────────────
  AIRTIME: 'Airtime',
  DATA_BUNDLE: 'Data bundle',
  CASH: 'Cash',
  TRANSPORT_REIMBURSEMENT: 'Transport reimbursement',
  MEAL: 'Meal',
  STIPEND: 'Stipend',
  MERCHANDISE: 'Merchandise',
  TRAINING: 'Training',
  COORDINATOR_STIPEND: 'Coordinator stipend',
  COMMUNICATION_COST: 'Communication cost',
  TRANSPORT_COST_REIMBURSEMENT: 'Transport cost reimbursement',
  MEETING_ATTENDANCE: 'Meeting attendance',
  TRAINING_ATTENDANCE: 'Training attendance',
  ACCESSIBILITY_SUPPORT: 'Accessibility support',
  MOBILE_MONEY: 'Mobile money',
  AIRTIME_TOPUP: 'Airtime top-up',
  BANK_TRANSFER: 'Bank transfer',
  IN_KIND: 'In kind',
  VOUCHER: 'Voucher',

  // ── transport matching ──────────────────────────────────────────────────
  MEMBER_VEHICLE: "A member's vehicle",
  WALKING_GROUP: 'Walking group',
  MOBILITY_NEED: 'Mobility need',
  DISTANCE: 'Distance',
  AGE: 'Age',
  MEDICAL: 'Medical',
  AVAILABLE: 'Available',
  FULL: 'Full',
  WITHDRAWN: 'Withdrawn',
  COMPLETED: 'Completed',
  OPEN: 'Open',
  MATCHED: 'Matched',
  IN_TRANSIT: 'In transit',
  CANCELLED: 'Cancelled',
  UNFULFILLED: 'Unfulfilled',
}

/** A code the server sent → words. Unknown codes title-case rather than vanish. */
export function label(code?: string | null): string {
  if (!code) return '—'
  return LABELS[code] ?? code.charAt(0) + code.slice(1).toLowerCase().replace(/_/g, ' ')
}

/** For a `<select>` driven by a served array. */
export function options(codes: string[] | null | undefined) {
  return (codes ?? []).map((c) => ({ value: c, label: label(c) }))
}
